import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './marketing.css';

const TOC = [
  'Acceptance of Terms',
  'About Gantima + Scope of Services',
  'Client Engagement + Retainer',
  'Fees, Billing, and Payment',
  'Cancellation + Refunds',
  'Intellectual Property',
  'Confidentiality',
  'Third-Party Integrations + Client Data',
  'AI + Machine Learning Outputs',
  'Warranties + Disclaimers',
  'Limitation of Liability',
  'Indemnification',
  'Non-Solicitation',
  'Termination',
  'Governing Law + Dispute Resolution',
  'Changes to These Terms',
  'Contact',
];

export default function Terms() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(null);
  const toggle = (i) => setOpen(open === i ? null : i);

  const sections = [
    {
      title: '1. Acceptance of Terms',
      body: (
        <>
          <p>These Terms of Service ("Terms") govern your access to and use of the website www.gantima.com and any consulting, automation, custom-development, or SaaS services provided by Gantima Software ("Gantima", "we", "us", "our").</p>
          <p style={{marginTop:'12px'}}>By engaging Gantima for services, accessing www.gantima.com, or signing a Statement of Work (SOW) with us, you ("Client", "you") accept these Terms in full. If you are entering into these Terms on behalf of a company, you represent that you have authority to bind that company.</p>
          <p style={{marginTop:'12px'}}><strong style={{color:'#e2e8f0'}}>Please read Section 15 carefully — it requires most disputes to be resolved by binding individual arbitration and waives class-action rights.</strong></p>
        </>
      ),
    },
    {
      title: '2. About Gantima + Scope of Services',
      body: (
        <>
          <p>Gantima Software is an AI Automation Agency. Our services include:</p>
          <ul style={{listStyle:'disc',paddingLeft:'20px',display:'flex',flexDirection:'column',gap:'8px',marginTop:'12px'}}>
            <li>Custom AI chatbot development</li>
            <li>Workflow automation (n8n, Make, Zapier, custom pipelines)</li>
            <li>Algorithmic trading bot development (crypto, forex, futures, equities)</li>
            <li>Invoice processing, HR, CRM, and other business process automation</li>
            <li>Full-stack AI SaaS product development</li>
            <li>Legal, compliance, and document processing automation</li>
            <li>Our own SaaS products, including Viral Volt (viralvolt.ai) — governed by <a href="https://viralvolt.ai/legal/terms.html" style={{color:'#4f8ef7'}}>Viral Volt's separate Terms of Service</a></li>
          </ul>
          <p style={{marginTop:'14px'}}>Specific deliverables, timelines, and scope for each project are defined in a written Statement of Work (SOW) or engagement letter signed by both parties. In case of conflict, the SOW controls over these Terms for that specific engagement.</p>
        </>
      ),
    },
    {
      title: '3. Client Engagement + Retainer',
      body: (
        <>
          <p>Client engagements typically follow this structure:</p>
          <ol style={{listStyle:'decimal',paddingLeft:'20px',display:'flex',flexDirection:'column',gap:'8px',marginTop:'12px'}}>
            <li><strong style={{color:'#e2e8f0'}}>Free Audit</strong>: 30-minute call, no commitment</li>
            <li><strong style={{color:'#e2e8f0'}}>Blueprint</strong>: written project plan with tools, integrations, timeline, and expected ROI (delivered within 3 business days)</li>
            <li><strong style={{color:'#e2e8f0'}}>Engagement Letter / SOW</strong>: signed before any billable work begins</li>
            <li><strong style={{color:'#e2e8f0'}}>Retainer</strong>: monthly recurring fee for ongoing services, or fixed-fee for one-off builds</li>
          </ol>
          <p style={{marginTop:'14px'}}>Retainer plans (Starter, Professional, Scale, Enterprise) have a <strong style={{color:'#e2e8f0'}}>3-month minimum commitment</strong> to allow sufficient ramp-up. After month 3, either party may cancel with 30 days' written notice.</p>
        </>
      ),
    },
    {
      title: '4. Fees, Billing, and Payment',
      body: (
        <>
          <p><strong style={{color:'#e2e8f0'}}>Retainer plans:</strong> billed monthly in advance on the same calendar day each month. Payment is due within 7 days of invoice.</p>
          <p style={{marginTop:'12px'}}><strong style={{color:'#e2e8f0'}}>Fixed-fee projects:</strong> typically billed 50% at engagement start, 50% on delivery. Custom milestone splits may be agreed in the SOW.</p>
          <p style={{marginTop:'12px'}}><strong style={{color:'#e2e8f0'}}>Payment methods:</strong> ACH, wire transfer, credit card via Stripe, or crypto (BTC/ETH/USDC) by prior agreement.</p>
          <p style={{marginTop:'12px'}}><strong style={{color:'#e2e8f0'}}>Late payment:</strong> invoices unpaid after 15 days accrue 1.5% monthly interest and may result in suspension of services until account is current.</p>
          <p style={{marginTop:'12px'}}><strong style={{color:'#e2e8f0'}}>Taxes:</strong> all fees are exclusive of sales tax, VAT, GST, or similar taxes, which are the Client's responsibility.</p>
          <p style={{marginTop:'12px'}}><strong style={{color:'#e2e8f0'}}>Third-party costs:</strong> Client is responsible for any third-party fees required to deliver the engagement (API costs, cloud hosting, SaaS subscriptions, etc.) unless otherwise stated in the SOW.</p>
        </>
      ),
    },
    {
      title: '5. Cancellation + Refunds',
      body: (
        <>
          <p><strong style={{color:'#e2e8f0'}}>Retainer cancellation:</strong> after the 3-month minimum commitment, either party may cancel with 30 days' written notice. Services continue during the notice period.</p>
          <p style={{marginTop:'12px'}}><strong style={{color:'#e2e8f0'}}>Fixed-fee cancellation:</strong> Client may cancel before delivery. Refunds are prorated to work completed at Gantima's standard hourly rate ($250/hr) or the SOW milestone breakdown, whichever produces the smaller refund. All work-in-progress remains Gantima's property until fully paid for.</p>
          <p style={{marginTop:'12px'}}><strong style={{color:'#e2e8f0'}}>Refunds:</strong> monthly retainer fees are non-refundable once the month has started. Fixed-fee refunds follow the prorated schedule above. Refunds required by applicable law will always be honored.</p>
          <p style={{marginTop:'12px'}}><strong style={{color:'#e2e8f0'}}>Termination for cause:</strong> either party may terminate immediately for material breach not cured within 15 days of written notice.</p>
        </>
      ),
    },
    {
      title: '6. Intellectual Property',
      body: (
        <>
          <p><strong style={{color:'#e2e8f0'}}>Client-owned:</strong> upon full payment, all custom deliverables built specifically for the Client under an SOW (source code, custom workflows, custom trained model weights, documentation) transfer to Client on a work-for-hire / assigned basis, except for the pre-existing components below.</p>
          <p style={{marginTop:'12px'}}><strong style={{color:'#e2e8f0'}}>Gantima-owned (pre-existing):</strong> our proprietary frameworks, internal libraries, methodologies, ARIA monitoring system, and any reusable components developed independently of Client's engagement remain Gantima's IP. We grant Client a perpetual, royalty-free license to use these components as embedded in the deliverables.</p>
          <p style={{marginTop:'12px'}}><strong style={{color:'#e2e8f0'}}>Third-party components:</strong> deliverables may include open-source libraries or third-party services, each governed by their own licenses.</p>
          <p style={{marginTop:'12px'}}><strong style={{color:'#e2e8f0'}}>Case studies + portfolio:</strong> unless otherwise agreed in writing, Gantima may reference Client's name, logo, and non-confidential project details in marketing materials, case studies, and portfolios.</p>
        </>
      ),
    },
    {
      title: '7. Confidentiality',
      body: (
        <>
          <p>Both parties will treat as confidential any non-public information disclosed during the engagement, including business plans, customer data, financials, source code, credentials, and strategy documents.</p>
          <p style={{marginTop:'12px'}}>Confidentiality obligations survive termination for <strong style={{color:'#e2e8f0'}}>5 years</strong>. For trade secrets, obligations persist as long as the information remains a trade secret under applicable law.</p>
          <p style={{marginTop:'12px'}}>Gantima will sign Client's standard NDA on request. Alternatively, we can provide our mutual NDA template. Custom NDAs are available for Enterprise plans.</p>
          <p style={{marginTop:'12px'}}>Access to Client systems is limited to team members required to perform the engagement. Credentials are stored in encrypted vaults (1Password Business, AWS Secrets Manager) and rotated when personnel change.</p>
        </>
      ),
    },
    {
      title: '8. Third-Party Integrations + Client Data',
      body: (
        <>
          <p>Automations we build often integrate with your existing tools (HubSpot, Salesforce, QuickBooks, Slack, Stripe, cloud providers, etc.). You are responsible for:</p>
          <ul style={{listStyle:'disc',paddingLeft:'20px',display:'flex',flexDirection:'column',gap:'8px',marginTop:'12px'}}>
            <li>Ensuring your use of Gantima-built automations complies with the terms of each third-party service</li>
            <li>Providing the necessary API keys, credentials, and access permissions</li>
            <li>Compliance with any data-protection laws applicable to the personal data you process through the automation (GDPR, CCPA, HIPAA, etc.)</li>
          </ul>
          <p style={{marginTop:'14px'}}>Gantima will act as a data processor (not controller) with respect to any personal data we process on your behalf during the engagement. On written request, we will sign a standard Data Processing Agreement (DPA).</p>
          <p style={{marginTop:'12px'}}>For HIPAA workflows, we can sign a Business Associate Agreement (BAA) on Professional plans and above.</p>
        </>
      ),
    },
    {
      title: '9. AI + Machine Learning Outputs',
      body: (
        <>
          <p>Many of our deliverables use third-party AI services (OpenAI GPT-4, Google Gemini, Anthropic Claude, or open-source models). Client acknowledges:</p>
          <ul style={{listStyle:'disc',paddingLeft:'20px',display:'flex',flexDirection:'column',gap:'8px',marginTop:'12px'}}>
            <li>AI outputs can be inaccurate, biased, or infringing — Client is responsible for reviewing outputs before use</li>
            <li>Purely AI-generated content may not be eligible for copyright protection in some jurisdictions</li>
            <li>Data submitted to AI providers may be processed in accordance with those providers' terms and privacy policies</li>
            <li>Gantima makes no warranty that AI outputs are original, accurate, or fit for any particular purpose</li>
          </ul>
          <p style={{marginTop:'14px'}}>Where possible, we architect solutions so that Client data is not used to train third-party AI models (e.g., using enterprise-tier API endpoints with no-training clauses).</p>
        </>
      ),
    },
    {
      title: '10. Warranties + Disclaimers',
      body: (
        <>
          <p><strong style={{color:'#e2e8f0'}}>Our warranty:</strong> we will perform services with reasonable skill and care consistent with industry standards, using qualified personnel. Custom builds carry a <strong style={{color:'#e2e8f0'}}>30-day bug-fix warranty</strong> from delivery for defects specifically caused by our workmanship.</p>
          <p style={{marginTop:'12px'}}><strong style={{color:'#e2e8f0'}}>What's excluded:</strong> failures caused by Client modifications, changes to third-party APIs, force majeure, or Client-provided data quality issues are outside the warranty.</p>
          <p style={{marginTop:'12px'}}><strong style={{color:'#e2e8f0'}}>Disclaimers:</strong> to the maximum extent permitted by law, our services and deliverables are provided "AS IS" and "AS AVAILABLE". Gantima disclaims all other warranties, express or implied, including merchantability, fitness for a particular purpose, and non-infringement. We do not guarantee any specific business outcome, revenue increase, cost savings, or ROI — while we design our automations to deliver measurable value, actual results depend on many factors outside our control.</p>
        </>
      ),
    },
    {
      title: '11. Limitation of Liability',
      body: (
        <>
          <p>To the maximum extent permitted by applicable law:</p>
          <ul style={{listStyle:'disc',paddingLeft:'20px',display:'flex',flexDirection:'column',gap:'10px',marginTop:'12px'}}>
            <li><strong style={{color:'#e2e8f0'}}>Direct damages:</strong> Gantima's aggregate liability under any engagement is limited to the fees paid by Client to Gantima in the <strong style={{color:'#e2e8f0'}}>12 months preceding</strong> the event giving rise to the claim.</li>
            <li><strong style={{color:'#e2e8f0'}}>Indirect damages:</strong> in no event will Gantima be liable for lost profits, lost revenue, lost business opportunity, loss of data, or any consequential, incidental, punitive, or special damages, even if advised of the possibility.</li>
            <li><strong style={{color:'#e2e8f0'}}>Trading systems:</strong> for algorithmic trading bots, Client acknowledges that all trading involves substantial risk of loss. Gantima is not liable for any trading losses, missed trades, exchange outages, or losses caused by third-party market data, brokerages, or exchange APIs.</li>
          </ul>
          <p style={{marginTop:'14px'}}>Some jurisdictions do not allow limitation of implied warranties or exclusion of certain damages. In those jurisdictions, our liability is limited to the maximum extent permitted by law.</p>
        </>
      ),
    },
    {
      title: '12. Indemnification',
      body: (
        <>
          <p><strong style={{color:'#e2e8f0'}}>Client indemnifies Gantima</strong> against any claims arising from (i) Client's use of deliverables, (ii) data Client provides to us, (iii) Client's violation of third-party rights or applicable law, and (iv) Client's breach of these Terms.</p>
          <p style={{marginTop:'12px'}}><strong style={{color:'#e2e8f0'}}>Gantima indemnifies Client</strong> against third-party claims that our original deliverables (excluding Client-provided content, third-party components, and modifications by Client) infringe a valid U.S. copyright or trademark, up to the liability cap in Section 11. Gantima has the right to modify or replace the infringing element, or refund fees paid for that deliverable.</p>
          <p style={{marginTop:'12px'}}>Indemnifying party controls the defense; indemnified party must give prompt notice and reasonable cooperation.</p>
        </>
      ),
    },
    {
      title: '13. Non-Solicitation',
      body: (
        <>
          <p>During the engagement and for <strong style={{color:'#e2e8f0'}}>12 months after termination</strong>, Client agrees not to directly hire, contract, or solicit any Gantima employee or contractor who materially worked on Client's engagement, without Gantima's prior written consent.</p>
          <p style={{marginTop:'12px'}}>This does not restrict general job advertisements or unsolicited applications from Gantima personnel.</p>
          <p style={{marginTop:'12px'}}>If Client breaches this section, Client agrees to pay Gantima a placement fee equal to <strong style={{color:'#e2e8f0'}}>50% of the individual's annualized compensation</strong> at Gantima at the time of departure, in liquidated damages.</p>
        </>
      ),
    },
    {
      title: '14. Termination',
      body: (
        <>
          <p><strong style={{color:'#e2e8f0'}}>Termination for convenience:</strong> either party may terminate a retainer with 30 days' written notice after the 3-month minimum.</p>
          <p style={{marginTop:'12px'}}><strong style={{color:'#e2e8f0'}}>Termination for cause:</strong> either party may terminate immediately if the other party materially breaches these Terms and fails to cure within 15 days of written notice, becomes insolvent, or files for bankruptcy.</p>
          <p style={{marginTop:'12px'}}><strong style={{color:'#e2e8f0'}}>Effect of termination:</strong> Client remains responsible for all fees accrued through termination. Gantima will deliver all completed work-product for which Client has paid and reasonably assist with knowledge transfer, at Gantima's standard hourly rate ($250/hr) for any transition work beyond 4 hours.</p>
          <p style={{marginTop:'12px'}}><strong style={{color:'#e2e8f0'}}>Survival:</strong> Sections 4, 5, 6, 7, 10, 11, 12, 13, 15, and 17 survive termination.</p>
        </>
      ),
    },
    {
      title: '15. Governing Law + Dispute Resolution',
      body: (
        <>
          <p><strong style={{color:'#e2e8f0'}}>Governing law:</strong> these Terms are governed by the laws of the State of Maryland, USA, without regard to conflict-of-law rules.</p>
          <p style={{marginTop:'12px'}}><strong style={{color:'#e2e8f0'}}>Informal resolution first:</strong> before filing any formal claim, the parties agree to attempt good-faith resolution by email or video call for at least 30 days.</p>
          <p style={{marginTop:'12px'}}><strong style={{color:'#e2e8f0'}}>Binding individual arbitration:</strong> any dispute not resolved informally will be settled by binding individual arbitration under the American Arbitration Association (AAA) Commercial Rules, seated in Waldorf, Maryland (or online, at the arbitrator's discretion). The arbitrator's decision is final.</p>
          <p style={{marginTop:'12px'}}><strong style={{color:'#e2e8f0'}}>CLASS-ACTION WAIVER:</strong> disputes must be brought individually, not as a plaintiff or class member in any purported class or representative proceeding. If this waiver is held unenforceable, then the entire arbitration clause is void.</p>
          <p style={{marginTop:'12px'}}><strong style={{color:'#e2e8f0'}}>Opt-out:</strong> new Clients may opt out of arbitration by emailing <a href="mailto:admin@gantima.com" style={{color:'#4f8ef7'}}>admin@gantima.com</a> within 30 days of first engaging Gantima, with subject "Arbitration Opt-Out".</p>
          <p style={{marginTop:'12px'}}><strong style={{color:'#e2e8f0'}}>Injunctive relief:</strong> either party may seek injunctive relief in a court of competent jurisdiction for IP infringement, confidentiality breaches, or non-solicitation violations without first resorting to arbitration.</p>
        </>
      ),
    },
    {
      title: '16. Changes to These Terms',
      body: (
        <>
          <p>We may update these Terms from time to time. Material changes will be announced by email to active Clients at least <strong style={{color:'#e2e8f0'}}>30 days before</strong> taking effect. Non-material changes (formatting, contact info updates, typo fixes) take effect immediately.</p>
          <p style={{marginTop:'12px'}}>Continuing to use our services after the effective date of a change constitutes acceptance of the updated Terms. If you disagree with a material change, you may terminate under Section 14 without additional obligation for the notice period.</p>
          <p style={{marginTop:'12px'}}>The "Last updated" date at the top reflects the current version.</p>
        </>
      ),
    },
    {
      title: '17. Contact',
      body: (
        <>
          <p>Questions about these Terms:</p>
          <ul style={{listStyle:'disc',paddingLeft:'20px',display:'flex',flexDirection:'column',gap:'8px',marginTop:'12px'}}>
            <li>General: <a href="mailto:support@gantima.com" style={{color:'#4f8ef7'}}>support@gantima.com</a></li>
            <li>Sales: <a href="mailto:sales@gantima.com" style={{color:'#4f8ef7'}}>sales@gantima.com</a></li>
            <li>Legal / arbitration opt-out: <a href="mailto:admin@gantima.com" style={{color:'#4f8ef7'}}>admin@gantima.com</a></li>
          </ul>
        </>
      ),
    },
  ];

  return (
    <div style={{background:'#020817',minHeight:'100vh',color:'#94a3b8',fontFamily:'-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif'}}>
      {/* Simple nav */}
      <nav style={{padding:'20px 40px',borderBottom:'1px solid rgba(79,142,247,0.1)'}}>
        <button onClick={()=>navigate('/')} style={{background:'none',border:'none',color:'#4f8ef7',cursor:'pointer',fontSize:'.95rem',fontWeight:700,display:'flex',alignItems:'center',gap:'8px',fontFamily:'inherit'}}>← Back to Gantima Software</button>
      </nav>

      <div style={{maxWidth:'820px',margin:'0 auto',padding:'56px 24px 96px'}}>
        <div style={{marginBottom:'10px'}}>
          <span style={{background:'linear-gradient(135deg,#4f8ef7,#7c3aed,#ec4899)',WebkitBackgroundClip:'text',backgroundClip:'text',color:'transparent',fontSize:'.75rem',fontWeight:800,letterSpacing:'2.5px',textTransform:'uppercase'}}>Terms of Service</span>
        </div>
        <h1 style={{fontSize:'2.4rem',fontWeight:900,color:'#eef2ff',lineHeight:1.15,letterSpacing:'-1.5px',marginBottom:'12px'}}>Gantima Software Terms of Service</h1>
        <p style={{color:'#94a3b8',fontSize:'.92rem',marginBottom:'28px'}}><strong>Last updated:</strong> August 2, 2026 · <strong>Effective:</strong> August 2, 2026</p>
        <p style={{color:'#c0c8dc',fontSize:'.98rem',lineHeight:1.7,marginBottom:'32px'}}>
          These Terms govern your use of Gantima Software's services and www.gantima.com.
          Please read Section 15 carefully — it requires most disputes to be resolved by binding individual arbitration and waives class-action rights.
        </p>

        {/* Table of Contents */}
        <div style={{background:'#0a1628',border:'1px solid rgba(79,142,247,0.15)',borderRadius:'16px',padding:'24px 28px',marginBottom:'40px'}}>
          <p style={{fontWeight:800,color:'#e2e8f0',marginBottom:'14px',fontSize:'.9rem',letterSpacing:'1px',textTransform:'uppercase'}}>Contents</p>
          <ol style={{listStyle:'none',display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:'8px'}}>
            {TOC.map((item, i) => (
              <li key={i}>
                <button
                  onClick={() => setOpen(i)}
                  style={{background:'none',border:'none',color:'#4f8ef7',fontSize:'.83rem',cursor:'pointer',textAlign:'left',padding:'2px 0',fontFamily:'inherit'}}
                >
                  {i+1}. {item}
                </button>
              </li>
            ))}
          </ol>
        </div>

        {/* Accordion Sections */}
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          {sections.map((s, i) => (
            <div key={i} style={{background:'#0a1628',border:`1px solid ${open===i ? 'rgba(79,142,247,0.4)' : 'rgba(79,142,247,0.12)'}`,borderRadius:'14px',overflow:'hidden',transition:'border-color .2s'}}>
              <button
                onClick={() => toggle(i)}
                style={{width:'100%',display:'flex',justifyContent:'space-between',alignItems:'center',padding:'18px 24px',background:'none',border:'none',cursor:'pointer',fontFamily:'inherit',textAlign:'left'}}
              >
                <span style={{fontWeight:800,fontSize:'1rem',color:'#e2e8f0'}}>{s.title}</span>
                <span style={{color:'#4f8ef7',fontSize:'1.2rem',transform: open===i ? 'rotate(180deg)' : 'rotate(0)',transition:'transform .2s',flexShrink:0,marginLeft:'16px'}}>▾</span>
              </button>
              {open === i && (
                <div style={{padding:'0 24px 24px',color:'#94a3b8',lineHeight:1.8,fontSize:'.9rem'}}>
                  {s.body}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer CTA */}
        <div style={{marginTop:'56px',padding:'32px',background:'#0a1628',border:'1px solid rgba(79,142,247,0.15)',borderRadius:'16px',textAlign:'center'}}>
          <p style={{color:'#94a3b8',marginBottom:'20px'}}>Questions about these Terms or a specific engagement? Our team is happy to walk you through anything.</p>
          <div style={{display:'flex',gap:'12px',justifyContent:'center',flexWrap:'wrap'}}>
            <a href="mailto:admin@gantima.com" className="mkt-btn mkt-btn-outline">📧 Email Legal Team</a>
            <button className="mkt-btn mkt-btn-primary" onClick={() => navigate('/')}>← Back to Home</button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="mkt-footer">
        <div className="mkt-container">
          <div className="mkt-footer-bottom">
            <span>© 2026 Gantima Software. All rights reserved.</span>
            <span>
              <a href="/privacy" onClick={e=>{e.preventDefault();navigate('/privacy');}} style={{color:'#64748b'}}>Privacy Policy</a>
              {' · '}
              <a href="/terms" onClick={e=>{e.preventDefault();navigate('/terms');}} style={{color:'#64748b'}}>Terms of Service</a>
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
